import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSubmitA2pBrandMutation, useRefreshA2pStatusMutation, useGetHomeQuery, useResendA2pOtpMutation } from "@/store/rest_api";
import A2pRegistration from "@/model/interface/A2pRegistration";
import A2pPricingSummary from "./A2pPricingSummary";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle, Clock, XCircle } from "lucide-react";

interface Props {
  registration: A2pRegistration;
}

export default function BrandReviewStep({ registration }: Props) {
  const [submitBrand, { isLoading: isSubmitting }] = useSubmitA2pBrandMutation();
  const [refreshStatus, { isLoading: isRefreshing }] = useRefreshA2pStatusMutation();
  const { data: home } = useGetHomeQuery();
  const soleProprietor = home?.activeOrganisation?.businessType === "SOLE_PROPRIETOR";
  const [resendOtp, { isLoading: isResendingOtp }] = useResendA2pOtpMutation();

  const handleResendOtp = async () => {
    try {
      await resendOtp().unwrap();
      toast.success("Verification text sent");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to send verification text");
    }
  };

  const handleSubmitBrand = async () => {
    try {
      await submitBrand().unwrap();
      toast.success("Brand registration submitted");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to submit brand registration");
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshStatus().unwrap();
      toast.success("Status refreshed");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to refresh status");
    }
  };

  const profileApproved = registration.customerProfileStatus?.includes("approved");
  const brandSubmitted = !!registration.brandRegistrationSid;
  const canSubmitBrand = !brandSubmitted || registration.brandStatus === "FAILED";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Brand Registration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Customer Profile Status */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Customer Profile</h3>
          <div className="flex items-center gap-3">
            {profileApproved ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : registration.customerProfileStatus?.includes("rejected") ? (
              <XCircle className="h-5 w-5 text-red-600" />
            ) : (
              <Clock className="h-5 w-5 text-yellow-600" />
            )}
            <span className="text-sm">
              SID: <code className="text-xs">{registration.customerProfileSid || "Not submitted"}</code>
            </span>
            <Badge variant={profileApproved ? "default" : "secondary"}>
              {registration.customerProfileStatus || "Pending"}
            </Badge>
          </div>
        </div>

        {/* Trust Product Status */}
        {registration.trustProductSid && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Trust Product</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm">
                SID: <code className="text-xs">{registration.trustProductSid}</code>
              </span>
              <Badge variant="secondary">{registration.trustProductStatus || "Pending"}</Badge>
            </div>
          </div>
        )}

        {/* Brand Registration Status */}
        {brandSubmitted && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Brand Registration</h3>
            <div className="flex items-center gap-3">
              {registration.brandStatus === "APPROVED" ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : registration.brandStatus === "FAILED" ? (
                <XCircle className="h-5 w-5 text-red-600" />
              ) : (
                <Clock className="h-5 w-5 text-yellow-600" />
              )}
              <span className="text-sm">
                SID: <code className="text-xs">{registration.brandRegistrationSid}</code>
              </span>
              <Badge variant={registration.brandStatus === "APPROVED" ? "default" : "secondary"}>
                {registration.brandStatus || "Pending"}
              </Badge>
              {registration.brandScore != null && (
                <Badge variant="outline">Score: {registration.brandScore}</Badge>
              )}
            </div>
          </div>
        )}

        {registration.rejectionReason && (
          <Alert variant="destructive">
            <AlertDescription>{registration.rejectionReason}</AlertDescription>
          </Alert>
        )}

        {soleProprietor && brandSubmitted && (
          <Alert>
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>
                {registration.proofStatus === "VERIFIED"
                  ? "Owner mobile verified."
                  : `We texted a verification code to ${registration.proofMobile || "the owner's mobile"}. Reply to that text to verify your brand (within 24 hours).`}
              </span>
              {registration.proofStatus !== "VERIFIED" && (
                <Button variant="outline" size="sm" onClick={handleResendOtp} disabled={isResendingOtp}>
                  {isResendingOtp && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
                  Resend code
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {!profileApproved && !brandSubmitted && (
          <Alert>
            <AlertDescription>
              Your customer profile is under review. This usually takes a few minutes,
              but can take up to a few business days if a manual review is needed.
              Click "Refresh Status" to check for updates.
            </AlertDescription>
          </Alert>
        )}

        {profileApproved && !brandSubmitted && (
          <Alert>
            <AlertDescription>
              Your customer profile has been approved. You can now submit your brand for registration.
            </AlertDescription>
          </Alert>
        )}

        {canSubmitBrand && (
          <A2pPricingSummary
            soleProprietor={soleProprietor}
            skipAutomaticSecVet={!!registration.skipAutomaticSecVet}
            balance={home?.activeOrganisation?.balance}
          />
        )}
      </CardContent>
      <CardFooter className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
          {isRefreshing ? <Loader2 className="animate-spin mr-1 h-4 w-4" /> : <RefreshCw className="mr-1 h-4 w-4" />}
          Refresh Status
        </Button>
        {canSubmitBrand && (
          <Button onClick={handleSubmitBrand} disabled={isSubmitting || !profileApproved}>
            {isSubmitting && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
            {isSubmitting
              ? "Submitting..."
              : registration.brandStatus === "FAILED"
                ? "Resubmit Brand Registration"
                : "Submit Brand Registration"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
